import { lazy, Suspense, useEffect, useMemo, useState } from 'react'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import { systemValueLabel } from '../../generic/systemValues/systemValueLabel'
import { Button, Chip, FormHelperText, IconButton, Typography, useTheme } from '@mui/material'
import { apiGet } from '../../generic/Api_Functions'
import { AddCircleRounded, InsertDriveFile, ListAltOutlined, Sort, SquareFoot, TextSnippetOutlined } from '@mui/icons-material'
import { CheckCorrectTimeFormat, GetUtcTime } from '../../generic/DateTimeFunctions'
import SmartMateriaUITable from '../../generic/SmartMateriaUITable'
import AddProgramDialog from './dialogs/AddProgramDialog'
import TaskSortDialog from './dialogs/TaskSortDialog'
import TaskToolInfoDialog from './TaskToolInfoDialog'
import { SmartSelect } from '../../generic/smartSelect'
import { SmartDialog } from '../../generic/utilities/SmartDialog'
import { DefaultAccordion } from '../../generic/DefaultAccordion'
import { useAuthorization } from '../../generic/hooks/useAuthorization'
import { getRandomUUID } from '../../generic/utilities/randomuuid'
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered'
import { useToggle } from '../../generic/hooks/useToggle'
import { buildInstructionsDialogTitle, buildLinkedJobDocumentReference } from './jobTaskIdentity'
import { isExternalEffective } from './jobExternalCharacter'
import { isRowOpen } from './openRowGuard'
import { machineTimeExceedsDuration, timeFormatError } from './machineTime'

/*
 * The task instruction editor drags in a rich-text editor, and the document viewer a 3D one. Loading
 * them with this module made every screen that merely SHOWS a task table pay for both — which is what
 * the process tree started doing in story #150. They are only needed once their dialog is actually
 * opened, so they are loaded then, and the dialogs below are mounted only while open.
 */
const TaskInstructionDialog = lazy(() => import('./TaskInstruction').then((module) => ({ default: module.TaskInstructionDialog })))
const Document = lazy(() => import('../document/Document'))
const FeatureGroupsDialog = lazy(() => import('./feature/FeatureGroupsDialog'))

/**
 * @typedef {import('../../generic/utilities/objectStructures/workflow/workflow').Node}Node Node object
 * @typedef {import('../../generic/utilities/objectStructures/workflow/workflow').TaskReference} TaskReference TaskReference object
 */

/**
 * The task table of a job, with every dialog that hangs off it: order, programs, tool info, feature
 * groups, instructions and documents.
 *
 * Extracted from `JobTask` (story #150, D16) so the process tree can mount THE SAME table the graph
 * shows, in its side panel and in its job dialog, instead of the card list that drifted out of parity
 * with it. `JobTask` keeps the general properties of the node and mounts this component underneath.
 *
 * The props that only `NodeGeneralProperties` consumes (`updateJobReference`, `progressCalculationTypes`,
 * `cascadeApprovalEnabled`, `fabricationOrderOperable`) deliberately do NOT travel here.
 *
 * @param {Object} props
 * @param {Node} props.node - Current node
 * @param {boolean} props.editing - Is editing enabled
 * @param {function(TaskReference[])} props.updateTaskList - Function to update the task list
 * @param {function(boolean)} [props.onEditingRowChanged] - Reports whether this table holds an open row
 * @param {string} [props.workflowName] - Name of the workflow
 * @param {Object} [props.partReference] - PartReference object
 * @param {function(string)} [props.getTempUUID] function to get temporally assigned UUID for a given UUID
 * @param {function(string)} [props.setTempUUID] function to set new temporal UUID
 * @param {import('../../generic/utilities/objectStructures/globalStructures').IdName[]} [props.indexValues]
 * @param {boolean} [props.workflowUseIndex]
 * @param {boolean} [props.showMachineTime] Story 208: the SHOW_TASK_MACHINE_TIME setting; the machine time column is hidden (not removed) without it
 * @param {boolean} [props.featureGroupRevisionControlled] Story 186: CONTROLLED feature group revisions, handed to FeatureGroupsDialog
 * @param {Object<string, object>} [props.featureGroupBaselines] Story 186: persisted copy of each feature group by id, handed to FeatureGroupsDialog
 * @returns {JSX.Element}
 */
const JobTaskTable = ({
  node,
  editing,
  updateTaskList,
  // RN117.0: reports whether THIS table holds an open row. It replaces a setter of someone else's
  // boolean, which no host but the graph could use.
  onEditingRowChanged = () => {},
  // Defaulted so any host that does not thread them through still mounts: `getTempUUID` is called in
  // the body of the JSX, not inside a handler, so a missing one throws on the very first render.
  workflowName = '',
  partReference = null,
  getTempUUID = () => undefined,
  setTempUUID = () => {},
  indexValues = [],
  workflowUseIndex = false,
  showMachineTime = false,
  featureGroupRevisionControlled = false,
  featureGroupBaselines = {},
  // Quien la monta estrecha lo dice; por defecto va como siempre.
  compact = false
}) => {
  const [editingTask, setEditingTask] = useState(null)

  /*
   * RN117.3.2: a host that goes away with its row open leaves the guard's set on the way out. Without
   * this, closing the job window — or collapsing the panel that holds this table — with a row open
   * would leave the process impossible to save and nothing on screen to say why, which is worse than
   * the defect the guard exists to fix.
   */
  useEffect(() => () => onEditingRowChanged(false), [onEditingRowChanged])
  const [openSortDialog, setOpenSortDialog] = useState(false)
  const [toolDialog, setToolDialog] = useState({ open: false, task: null })
  const [addProgramDialog, setProgramDialog] = useState({ open: false, multiple: false })
  const { t_ } = useSmartTranslation()
  const [showNodeDocuments, setShowNodeDocuments] = useState(false)
  const [formattedJob, setFormattedJob] = useState(null)
  const [isDownloadingFile, setIsDownloadingFile] = useState(false)
  const [showDocument, setShowDocument] = useState(false)
  const [taskDocument, setTaskDocument] = useState(null)
  const theme = useTheme()
  const isUserAuthorized = useAuthorization()
  const [instructionsDialog, setInstructionsDialog] = useState({ open: false })
  const [featureGroupDialog, setFeatureGroupDialog] = useState({ open: false })
  const [documentsClosedToogle, onDocumentsClosedToogle] = useToggle()
  const [isAddingNewRow, setIsAddingNewRow] = useState(false)

  // The minimum width belongs to the DOCUMENTS dialog container below, not to the table: the table
  // itself sets no minimum, which is what lets the tree's side panel host it (story #150, F13).
  const style = {
    minWidth: '80rem',
    width: '100%',
    display: 'grid',
    gap: '1rem',
    backgroundColor: theme.palette.background.row2,
    padding: '1rem'
  }

  const isEditingRow = (row) => {
    if (!editingTask) {
      return false
    }
    return editingTask['smart-table-index'] === row['smart-table-index']
  }

  // Use duplicated list to avoid changing original data until is saved
  const rows = useMemo(() => {
    if (!node?.jobReference?.taskReferences) {
      return []
    }
    return node.jobReference.taskReferences
  }, [node?.jobReference?.taskReferences])

  const updateTask = (newTask) => {
    const newTaskList = [...node.jobReference.taskReferences]
    let index = newTaskList.findIndex(task => task.uuid === newTask.uuid)
    if (index === undefined) {
      index = newTask['smart-table-index']
    }
    newTaskList[index] = newTask
    updateTaskList(newTaskList)
  }

  const linkedJobDocumentFunction = () => {
    return (
      <IconButton
        color='primary'
        onClick={() => {
          if (node && !getTempUUID(node.id)) {
            setTempUUID(node.id)
          }
          // S34 (RN10.2/RN01.4): identify the selected node by its own code+name (never node.name
          // alone, which is now the free descriptive field, often empty).
          const formatNode = buildLinkedJobDocumentReference(node, workflowName)
          setShowNodeDocuments(true)
          setFormattedJob(formatNode)
        }}
      >
        <TextSnippetOutlined />
      </IconButton>
    )
  }

  // HU-03 (S61/RN25.1): a subcontracted JOB has no duration of ours to configure. The trigger is the
  // EFFECTIVE external character, not the mark alone (RN26), and it is resolved by the single owner of
  // the predicate so this column can never disagree with the preparation fields or the node colour.
  const durationReadOnly = isExternalEffective(node?.jobReference)

  const columns = [
    {
      name: t_('Task order'),
      field: 'taskOrder',
      render: (row) => {
        return (
          <>
            <Typography>{row.taskOrder}</Typography>
          </>
        )
      }
    },
    {
      name: t_('Task name'),
      field: 'name'
    },
    {
      name: t_('Duration') + '(hh:mm:ss)',
      field: 'theoreticalTime',
      placeholder: 'hh:mm:ss',
      // S61/CA8: the first of the five fields of HU-03. The condition rides on the column itself, which
      // the generic table already honours — the two columns below it use the same property statically.
      // It depends on the JOB, not on the task in the row, so it is a value and not a per-row function.
      // S67/DN-4 is a DECLARED LIMIT: this covers the direct edition of the column ONLY. Creating a task
      // and deriving durations from *Operations* still set the duration, and that is expected.
      readOnly: durationReadOnly,
      // D8 (S49/S50): with the setting ON, the strict hh:mm:ss checked on every keystroke. With it OFF (DD8-2)
      // nothing changes for the installation: the shared check of before the amendment.
      condition: (row) => {
        return showMachineTime ? timeFormatError(row.theoreticalTime, t_) : CheckCorrectTimeFormat(row.theoreticalTime, t_)
      }
    },
    {
      // Story 208 (S31): the informative machine time, right of the duration. Hiding is purely visual
      // (CA6): the value stays on the task and travels with it on every save.
      name: t_('Machine time') + '(hh:mm:ss)',
      field: 'deviceTime',
      placeholder: 'hh:mm:ss',
      // S42 (RN6): the same predicate as the duration, resolved by the single owner of it above.
      readOnly: durationReadOnly,
      hide: !showMachineTime,
      // S36/D8: the very format check of the duration. An empty value (or a task that never had the field) is
      // valid: it means "no data".
      condition: (row) => {
        // S39 (D1): with the setting OFF the column is not on screen, so nothing about it may block a row.
        if (!showMachineTime) {
          return ''
        }
        const formatError = timeFormatError(row.deviceTime, t_)
        if (formatError) {
          return formatError
        }
        // S37 (RN3/V2): never longer than the duration being edited in the SAME row; equal is allowed.
        return machineTimeExceedsDuration(row.deviceTime, row.theoreticalTime) ? t_('Machine time cannot be greater than the duration') : ''
      }
    },
    {
      name: t_('Tool'),
      field: 'toolReferenceList.name',
      readOnly: true,
      render: (row) => {
        const taskToolInfos = isEditingRow(row) ? editingTask.taskToolInfos : row.taskToolInfos
        // An entry without its toolReference is dropped, not read through: a direct access throws and
        // takes the whole render down, losing the task instead of one tool name (S70, E111).
        let values = taskToolInfos ? taskToolInfos.map((taskToolInfo) => taskToolInfo.toolReference?.name).filter(Boolean) : []
        // Unique values
        values = [...new Set(values)]
        return (
          <SmartSelect
            readOnly
            multiple
            value={values}
            selectableOptions={values}
            helperText={isEditingRow(row) ? ' ' : undefined}
          />
        )
      }
    },
    {
      name: t_('Program'),
      field: 'defaultProgram.name',
      readOnly: true,
      render: (row) => {
        const editing = isEditingRow(row)
        const programName = editing ? editingTask?.defaultProgram?.name : row.defaultProgram?.name
        return (
          <>
            {programName}
            {editing && (
              <>
                <IconButton onClick={() => setProgramDialog({ open: true, multiple: false })}>
                  <InsertDriveFile />
                </IconButton>
                <FormHelperText>{' '}</FormHelperText>
              </>
            )}
          </>
        )
      }
    },
    {
      name: t_('Feature groups'),
      field: 'featureGroups',
      render: (row) => {
        if (!row.featureGroups) {
          return null
        }

        return row.featureGroups.map((featureGroup) =>
          <Chip
            key={featureGroup.id}
            label={
              <Typography
                sx={{
                  color: featureGroup.indexValue && featureGroup.indexValue?.name === 'PROPOSAL'
                    ? 'yellow.main'
                    : featureGroup.indexValue && featureGroup.indexValue?.name === 'APPROVED'
                      ? 'green'
                      : undefined
                }}
              >{featureGroup.code}
              </Typography>
            }
          />)
      },
      hide: isAddingNewRow
    }
  ]

  function isEmpty (obj) {
    for (const prop in obj) {
      if (Object.hasOwn(obj, prop)) {
        return false
      }
    }
    return true
  }

  const actions = {
    create: {
      label: 'Add task',
      fetchFunction: async (row) => {
        const order = Math.max(0, ...node.jobReference.taskReferences.map(e => e.taskOrder)) + 1
        const newTask = {
          taskOrder: order,
          // A job with no technology has no name to derive from, and reading through it threw
          // (RN113.1/S6). It falls back to the order alone, which is what the tree needs to admit a
          // task on a technology-less job — and the graph gets the same guard for free.
          name: row.name || (node.jobReference.technology?.name ? systemValueLabel(t_, 'technology', node.jobReference.technology) + '_' + order : String(order)), // P12: in the creator's language
          theoreticalTime: row.theoreticalTime || '00:00:01',
          // Story 208 (S40): the typed machine time, or "no data" (null at the API boundary), never "00:00:00".
          deviceTime: row.deviceTime || null,
          taskToolInfos: [],
          defaultProgram: editingTask.defaultProgram && !isEmpty(editing.defaultProgram) ? editingTask.defaultProgram : null,
          featureGroups: [],
          uuid: getRandomUUID()
        }
        const newTaskList = [...node.jobReference.taskReferences, newTask]
        updateTaskList(newTaskList)
      },
      disabled: !editing || !isUserAuthorized('ADD_TASK')
    },
    edit: {
      fetchFunction: (row) => {
        const newTask = {
          ...row,
          defaultProgram: editingTask.defaultProgram && !isEmpty(editingTask.defaultProgram) ? editingTask.defaultProgram : null,
          theoreticalTime: editingTask.theoreticalTime,
          // Story 208 (S35): an emptied cell is "no data", which Sun stores as NULL, never as "00:00:00"
          // (that would be a real zero). `null` is the API boundary value, not a default of ours.
          deviceTime: editingTask.deviceTime || null,
          taskToolInfos: editingTask.taskToolInfos,
          features: editingTask.featureList
        }
        updateTask(newTask)
      },
      condition: () => editing,
      disabled: !isUserAuthorized('EDIT_TASK')
    },
    delete: {
      fetchFunction: async (rows) => {
        const newTaskList = [...node.jobReference.taskReferences]
        newTaskList.splice(rows[0]['smart-table-index'], 1)
        updateTaskList(newTaskList)
        return { success: true }
      },
      condition: () => editing,
      disabled: !isUserAuthorized('DELETE_TASK')
    },
    custom: [
      {
        name: 'Feature groups',
        render: (row) => {
          return (
            <IconButton>
              <SquareFoot />
            </IconButton>
          )
        },
        onClick: (row) => {
          setFeatureGroupDialog({ open: true, taskReference: row })
        },
        disabled: !isUserAuthorized('FEATURE_GROUP')
      },
      {
        name: 'Instructions',
        render: (row) => {
          return (
            <IconButton>
              <FormatListNumberedIcon />
            </IconButton>
          )
        },
        onClick: (row) => {
          const task = node.jobReference.taskReferences.find(taskReference => taskReference.uuid === row.uuid)
          onDocumentsClosedToogle()
          // S34 (RN10.2/RN01.4): identify the node by its own code+name in the dialog title.
          setInstructionsDialog({ open: true, task, title: buildInstructionsDialogTitle(t_, node, row.name) })
        }
      },
      {
        name: 'Task Tool Info',
        render: (row) => {
          return (
            <IconButton>
              <ListAltOutlined />
            </IconButton>
          )
        },
        onClick: (row) => {
          const task = node.jobReference.taskReferences.find(taskReference => taskReference.uuid === row.uuid)
          setToolDialog({ open: true, task })
        },
        disabled: !isUserAuthorized('TASK_INFO')
      },
      {
        name: 'Documents',
        render: (row) => {
          return (
            <IconButton>
              <TextSnippetOutlined />
            </IconButton>
          )
        },
        disabled: (!isUserAuthorized('DOCUMENTS_TASK')),
        onClick: (row) => {
          setTempUUID(row.uuid)
          setTaskDocument(row)
          setShowDocument(true)
        }
      }
    ]
  }

  const defaultToolbar = {
    renderSticky: {
      fetchFunction: () => {
        return (
          <>
            {linkedJobDocumentFunction()}
            <Button
              sx={{ borderColor: 'transparent' }}
              color='primary'
              variant='outlined'
              disabled={!editing || editingTask != null}
              onClick={() => setOpenSortDialog(true)}
              startIcon={<Sort style={{ fontSize: '1.2em' }} />}
            >
              <Typography sx={{ fontSize: '0.875rem' }}>{t_('Change order')}</Typography>
            </Button>
            <Button
              sx={{ borderColor: 'transparent' }}
              color='primary'
              variant='outlined'
              disabled={!editing || editingTask != null}
              onClick={() => {
                setProgramDialog({ open: true, multiple: true })
              }}
              startIcon={<AddCircleRounded style={{ fontSize: '1.2em' }} />}
            >
              <Typography sx={{ fontSize: '0.875rem' }}>{t_('Add programs')}</Typography>
            </Button>
          </>
        )
      }
    }
  }

  const getToolTaskInfoFromOperation = (operationList) => {
    return operationList.map((operation) => {
      return {
        id: null,
        toolReference: { id: operation.toolReference.toolReferenceId, name: operation.toolReference.toolReferenceName, type: operation.toolReference.turn ? 'Turning' : 'Milling' },
        toolNumber: operation.toolNumber,
        duration: GetUtcTime(operation.duration),
        consumption: GetUtcTime(operation.duration),
        diameter: operation.diameter,
        length: operation.minimumLength,
        xaxisLength: operation.xaxisLength,
        yaxisLength: operation.yaxisLength,
        zaxisLength: operation.zaxisLength
      }
    })
  }

  const loadOperationList = async (programs) => {
    const programIdList = programs.map(program => parseInt(program.id))
    const operationList = await apiGet('getOperationByProgramIds/', { idList: programIdList })
    return operationList
  }

  const generateNewTaskFromProgram = (program, order, operationList) => {
    const task = {
      taskOrder: order,
      defaultProgram: program,
      theoreticalTime: '00:00:01',
      // Story 208 (S41): a task born from a program has no machine time until the user types one.
      deviceTime: null,
      name: systemValueLabel(t_, 'technology', program.technology) + '_' + order, // P12: in the creator's language
      featureGroups: [],
      taskToolInfos: [],
      toolRequired: false,
      isParsed: false,
      uuid: getRandomUUID()
    }
    if (operationList.length === 0) {
      return task
    }
    updateTaskWithOperations(task, operationList)
    return task
  }
  const updateTaskWithOperations = (task, operationList) => {
    if (operationList.length === 0) {
      setEditingTask(task)
      return
    }
    let theoreticalTime = 0
    operationList.forEach(operation => { theoreticalTime += operation.duration })
    task.theoreticalTime = GetUtcTime(parseInt(theoreticalTime))
    task.taskToolInfos = getToolTaskInfoFromOperation(operationList)
    setEditingTask(task)
  }

  const renderAccordion = (header, reference, defaultExpanded, resourceType) => {
    return (
      <DefaultAccordion headerRender={t_(header)} defaultExpanded={defaultExpanded}>
        <Document
          viewMode={!editing}
          reference={reference}
          resourceType={resourceType}
          isDownloadingFile={isDownloadingFile}
          setIsDownloadingFile={setIsDownloadingFile}
          referenceTempUUID={resourceType === 'JOB' && getTempUUID(node?.id)}
          referenceUUID={node?.id}
          // Dragging files here creates one document per file (default name, default type, appended
          // order), the same way the task instruction documents header already did.
          enableFileDrop
        />
      </DefaultAccordion>
    )
  }

  return (
    <>
      {featureGroupDialog.open && (
        <Suspense fallback={null}>
          <FeatureGroupsDialog
            open
            setOpenFalse={() => setFeatureGroupDialog({ taskReference: {}, open: false })}
            taskReference={featureGroupDialog.taskReference}
            updateTaskReference={(featureGroups) => {
              const newTask = { ...featureGroupDialog.taskReference, featureGroups }
              updateTask(newTask)
              setFeatureGroupDialog({ taskReference: {}, open: false })
            }}
            editing={editing}
            getTempUUID={getTempUUID}
            setTempUUID={setTempUUID}
            indexValues={indexValues}
            workflowUseIndex={workflowUseIndex}
            featureGroupRevisionControlled={featureGroupRevisionControlled}
            featureGroupBaselines={featureGroupBaselines}
          />
        </Suspense>
      )}
      {instructionsDialog.open && (
        <Suspense fallback={null}>
          <TaskInstructionDialog
            open
            task={instructionsDialog.task}
            updateTask={updateTask}
            closeCallback={() => setInstructionsDialog({ ...instructionsDialog, open: false })}
            openDocuments={() => {
              setTempUUID(instructionsDialog.task.uuid)
              setTaskDocument(instructionsDialog.task)
              setShowDocument(true)
            }}
            getTempUUID={getTempUUID}
            editing={editing}
            title={instructionsDialog.title}
            forceReloadDocuments={documentsClosedToogle}
          />
        </Suspense>
      )}
      {showDocument && (
        <Suspense fallback={null}>
          <SmartDialog
            closeCallback={() => {
              onDocumentsClosedToogle()
              setShowDocument(false)
            }}
            accept={false}
            cancel={false}
            close
            disableButtons={isDownloadingFile}
            renderComponent={
              <Document
                viewMode={!editing}
                reference={taskDocument}
                resourceType='TASK'
                isDownloadingFile={isDownloadingFile}
                setIsDownloadingFile={setIsDownloadingFile}
                referenceTempUUID={getTempUUID(taskDocument?.uuid)}
                referenceUUID={taskDocument?.uuid}
                enableFileDrop
              />
            }
            setOpen={showDocument}
          />
        </Suspense>
      )}
      {showNodeDocuments && (
        <Suspense fallback={null}>
          <SmartDialog
            closeCallback={() => setShowNodeDocuments(false)}
            accept={false}
            cancel={false}
            close
            disableButtons={isDownloadingFile}
            renderComponent={
              <div style={style}>
                {renderAccordion('Job', formattedJob, true, 'JOB')}
                {partReference?.id !== null && partReference?.id !== undefined ? renderAccordion('Part Reference', { id: partReference.id, code: partReference.code, name: partReference.name }, false, 'PART_REFERENCE') : null}
              </div>
            }
            setOpen={showNodeDocuments}
          />
        </Suspense>
      )}
      <AddProgramDialog
        open={addProgramDialog.open}
        setOpen={() => setProgramDialog({ ...addProgramDialog, open: false })}
        multipleMode={addProgramDialog.multiple}
        technology={node?.jobReference?.technology}
        onAddPrograms={async (programs) => {
          if (programs.length === 0) {
            setProgramDialog({ ...addProgramDialog, open: false })
            return
          }
          const currentOrder = Math.max(0, ...node.jobReference.taskReferences.map(e => e.taskOrder)) + 1
          const operationList = await loadOperationList(programs)
          const newTasks = programs.map((program, index) => generateNewTaskFromProgram(program, (currentOrder + index), operationList.filter((operation) => operation.program.id === program.id)))
          const newTaskList = [...node.jobReference.taskReferences, ...newTasks]
          updateTaskList(newTaskList)
          setProgramDialog({ ...addProgramDialog, open: false })
        }}
        onUpdateProgram={async (program) => {
          const newTask = { ...editingTask, defaultProgram: program }
          const operationList = await loadOperationList([program])
          updateTaskWithOperations(newTask, operationList)
          setProgramDialog({ ...addProgramDialog, open: false })
        }}
      />
      <SmartMateriaUITable
        columns={columns}
        dataFetch={rows}
        toolbar={defaultToolbar}
        multipleOptions
        dense
        // D8 (S52, DD8-1): a task that arrives over the bound (A3) is not confirmed untouched.
        validateConditionsOnConfirm
        // PO review 2026-09-23: la MISMA tabla la monta el grafo a lo ancho y el panel del arbol en 240
        // px. `compactHeader` es la palanca que la tabla ya trae para eso —barra de 48 px en vez de 100,
        // padding de celda a la mitad, fuentes e iconos mas pequenos— y no la usaba nadie en toda la app.
        // La decide QUIEN la monta: el grafo y la ventana la quieren ancha, el panel no cabe.
        compactHeader={compact}
        actions={actions}
        onEditIndexChanged={(tableIndex, row) => {
          setIsAddingNewRow(tableIndex === -1)
          // RN117.0: the ROW says whether one is open, not the index — adding reports (-1, newElement),
          // editing reports (index, row) and closing reports (-2) with no row at all.
          setEditingTask(row || null)
          onEditingRowChanged(isRowOpen(row))
        }}
        toolBarStyle={{ height: 'auto' }}
      />
      <TaskSortDialog
        open={openSortDialog}
        setOpen={setOpenSortDialog}
        taskList={rows}
        onSorted={(tasks) => {
          updateTaskList(tasks)
          setOpenSortDialog(false)
        }}
      />
      <TaskToolInfoDialog
        open={toolDialog.open}
        setOpen={(open) => setToolDialog({ ...toolDialog, open })}
        toolInfoList={toolDialog.task?.taskToolInfos}
        editing={editing}
        technology={node?.jobReference?.technology}
        isParsed={toolDialog.task?.isParsed}
        onToolInfoListUpdated={(toolInfoList) => {
          const newTask = { ...toolDialog.task, taskToolInfos: toolInfoList }
          setToolDialog({ ...toolDialog, task: newTask })
          updateTask(newTask)
        }}
      />
    </>
  )
}

export default JobTaskTable
